import { createSelector } from 'reselect';
import { denormalize } from 'normalizr';
import { CALL_API, HTTP_METHODS } from '../middlewares/api.js';
import { projectDashboard as projectDashboardSchema } from '../lib/schema.js';
import { selectEntities } from './entities.js';

const RETRIEVE_PROJECT_DASHBOARD_REQUEST =
  'changesync/project-dashboard/RETRIEVE_PROJECT_DASHBOARD_REQUEST';
const RETRIEVE_PROJECT_DASHBOARD_SUCCESS =
  'changesync/project-dashboard/RETRIEVE_PROJECT_DASHBOARD_SUCCESS';
const RETRIEVE_PROJECT_DASHBOARD_FAILURE =
  'changesync/project-dashboard/RETRIEVE_PROJECT_DASHBOARD_FAILURE';

const SEARCH_PEOPLE_ENGAGEMENT_REQUEST =
  'changesync/project-dashboard/SEARCH_PEOPLE_ENGAGEMENT_REQUEST';
const SEARCH_PEOPLE_ENGAGEMENT_SUCCESS =
  'changesync/project-dashboard/SEARCH_PEOPLE_ENGAGEMENT_SUCCESS';
const SEARCH_PEOPLE_ENGAGEMENT_FAILURE =
  'changesync/project-dashboard/SEARCH_PEOPLE_ENGAGEMENT_FAILURE';

const UPDATE_PEOPLE_ENGAGEMENT_QUERY =
  'changesync/project-dashboard/UPDATE_PEOPLE_ENGAGEMENT_QUERY';
const SEARCH_PEOPLE_ENGAGEMENT =
  'changesync/project-dashboard/SEARCH_PEOPLE_ENGAGEMENT';

const initialState = {
  dashboardIdsByProject: {},
  retrieveStatesByProject: {},
  peopleEngagement: {
    dataByProject: {},
    queryByProject: {},
    searchStatesByProject: {},
  },
};

export default (state = initialState, action) => {
  switch (action.type) {
    case RETRIEVE_PROJECT_DASHBOARD_REQUEST:
      return {
        ...state,
        retrieveStatesByProject: {
          ...state.retrieveStatesByProject,
          [action.meta.projectId]: {
            pending: true,
            success: false,
            error: null,
          },
        },
      };

    case RETRIEVE_PROJECT_DASHBOARD_SUCCESS:
      return {
        ...state,
        dashboardIdsByProject: {
          ...state.dashboardIdsByProject,
          [action.meta.projectId]: action.payload.result,
        },
        retrieveStatesByProject: {
          ...state.retrieveStatesByProject,
          [action.meta.projectId]: {
            pending: false,
            success: true,
            error: null,
          },
        },
      };

    case RETRIEVE_PROJECT_DASHBOARD_FAILURE:
      return {
        ...state,
        retrieveStatesByProject: {
          ...state.retrieveStatesByProject,
          [action.meta.projectId]: {
            pending: false,
            success: false,
            error: action.error,
          },
        },
      };

    case SEARCH_PEOPLE_ENGAGEMENT_REQUEST:
      return {
        ...state,
        peopleEngagement: {
          ...state.peopleEngagement,
          searchStatesByProject: {
            ...state.peopleEngagement.searchStatesByProject,
            [action.meta.projectId]: {
              pending: true,
              success: false,
              error: null,
            },
          },
        },
      };

    case SEARCH_PEOPLE_ENGAGEMENT_SUCCESS:
      return {
        ...state,
        peopleEngagement: {
          ...state.peopleEngagement,
          dataByProject: {
            ...state.peopleEngagement.dataByProject,
            [action.meta.projectId]: action.payload,
          },
          queryByProject: {
            ...state.peopleEngagement.queryByProject,
            [action.meta.projectId]: action.meta.query,
          },
          searchStatesByProject: {
            ...state.peopleEngagement.searchStatesByProject,
            [action.meta.projectId]: {
              pending: false,
              success: true,
              error: null,
            },
          },
        },
      };

    case SEARCH_PEOPLE_ENGAGEMENT_FAILURE:
      return {
        ...state,
        peopleEngagement: {
          ...state.peopleEngagement,
          searchStatesByProject: {
            ...state.peopleEngagement.searchStatesByProject,
            [action.meta.projectId]: {
              pending: false,
              success: false,
              error: action.error,
            },
          },
        },
      };

    case UPDATE_PEOPLE_ENGAGEMENT_QUERY:
      return {
        ...state,
        peopleEngagement: {
          ...state.peopleEngagement,
          queryByProject: {
            ...state.peopleEngagement.queryByProject,
            [action.meta.projectId]: action.payload,
          },
        },
      };

    default:
      return state;
  }
};

export const retrieveProjectDashboard = (projectId) => ({
  [CALL_API]: {
    types: [
      RETRIEVE_PROJECT_DASHBOARD_REQUEST,
      RETRIEVE_PROJECT_DASHBOARD_SUCCESS,
      RETRIEVE_PROJECT_DASHBOARD_FAILURE,
    ],
    endpoint: `projects/${projectId}/dashboard`,
    method: HTTP_METHODS.GET,
    schema: projectDashboardSchema,
  },
  meta: {
    projectId,
  },
});

export const updatePeopleEngagementQuery = (projectId, query) => ({
  type: UPDATE_PEOPLE_ENGAGEMENT_QUERY,
  payload: query,
  meta: {
    projectId,
  },
});

export const searchPeopleEngagement =
  (projectId, query = '') =>
  (dispatch) => {
    return dispatch({
      [CALL_API]: {
        types: [
          SEARCH_PEOPLE_ENGAGEMENT_REQUEST,
          SEARCH_PEOPLE_ENGAGEMENT_SUCCESS,
          SEARCH_PEOPLE_ENGAGEMENT_FAILURE,
        ],
        endpoint: `projects/${projectId}/dashboard/people_engagement_impact_overview`,
        method: HTTP_METHODS.GET,
        query: { search: query },
      },
      meta: {
        projectId,
        query,
        debounce: {
          time: 200,
          key: SEARCH_PEOPLE_ENGAGEMENT,
        },
      },
    });
  };

export const selectRetrieveDashboardState = (projectId) => (state) =>
  state.projectDashboard.retrieveStatesByProject[projectId] || {
    pending: false,
    success: false,
    error: null,
  };

const selectDashboardIdByProject = (projectId) => (state) =>
  state.projectDashboard.dashboardIdsByProject[projectId];

export const selectProjectDashboard = (projectId) =>
  createSelector(
    selectDashboardIdByProject(projectId),
    selectEntities,
    (dashboardId, entities) =>
      denormalize(dashboardId, projectDashboardSchema, entities)
  );

export const selectPeopleEngagementData = (projectId) => (state) =>
  state.projectDashboard.peopleEngagement.dataByProject[projectId] || [];

export const selectPeopleEngagementQuery = (projectId) => (state) =>
  state.projectDashboard.peopleEngagement.queryByProject[projectId] || '';

export const selectPeopleEngagementSearchState = (projectId) => (state) =>
  state.projectDashboard.peopleEngagement.searchStatesByProject[projectId] || {
    pending: false,
    success: false,
    error: null,
  };
